import {
  CheckCircle,
  XCircle,
  Warning,
  Hourglass,
  ShieldCheck,
} from '@phosphor-icons/react';

export interface StatusCounts {
  ok: number;
  blocked: number;
  error: number;
  approval: number;
  redacted: number;
}

interface SessionStatsBarProps {
  counts: StatusCounts;
  total: number;
}

/** Status segments — shown in the proportional bar (keyed by main outcome per call) */
const STATUS_SEGMENTS: {
  key: keyof StatusCounts;
  color: string;
  icon: typeof CheckCircle;
  label: string;
}[] = [
  { key: 'ok', color: 'var(--accent-teal)', icon: CheckCircle, label: 'allowed' },
  { key: 'blocked', color: 'var(--color-error)', icon: XCircle, label: 'blocked' },
  { key: 'error', color: 'var(--color-error)', icon: Warning, label: 'error' },
  { key: 'approval', color: 'var(--color-warning)', icon: Hourglass, label: 'pending' },
  { key: 'redacted', color: 'var(--accent-violet)', icon: ShieldCheck, label: 'redacted' },
];

export function SessionStatsBar({ counts, total }: SessionStatsBarProps) {
  const nonZeroStatus = STATUS_SEGMENTS.filter((s) => counts[s.key] > 0);

  return (
    <div className="flex flex-col gap-1.5 w-full max-w-[260px]">
      {/* Proportional bar */}
      <div className="flex h-2 w-full rounded-full overflow-hidden" style={{ background: 'var(--surface-3)' }}>
        {nonZeroStatus.map((seg) => {
          const pct = (counts[seg.key] / total) * 100;
          return (
            <div
              key={seg.key}
              style={{
                width: `${pct}%`,
                backgroundColor: seg.color,
                opacity: 0.85,
                minWidth: pct > 0 ? '4px' : 0,
              }}
            />
          );
        })}
      </div>

      {/* Count pills */}
      <div className="flex items-center gap-2.5 flex-wrap">
        {nonZeroStatus.map((seg) => {
          const Icon = seg.icon;
          return (
            <div key={seg.key} className="flex items-center gap-1">
              <Icon size={12} weight="fill" style={{ color: seg.color }} />
              <span className="text-[11px] font-medium" style={{ color: seg.color }}>
                {counts[seg.key]}
              </span>
              <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                {seg.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
